import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardTabs, type TabId } from './DashboardTabs';
import { OverviewTab } from './OverviewTab';
import { NeedsReviewTab } from './NeedsReviewTab';
import { PortfolioRiskTab } from './PortfolioRiskTab';
import { TeamMonitoringTab } from './TeamMonitoringTab';
import { PipelineActivityTab } from './PipelineActivityTab';
import { useReviewQueueStats } from '@/hooks/useDashboardStats';
import { usePortfolioRisk } from '@/hooks/usePortfolioRisk';
import { useAuth } from '@/hooks/useAuth';
import { SubmitContractDialog } from '@/pages/submit/SubmitContractDialog';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const VALID_TABS: TabId[] = ['overview', 'pipeline-activity', 'needs-review', 'portfolio-risk', 'team-monitoring'];

function isValidTab(value: string | null): value is TabId {
  return VALID_TABS.includes(value as TabId);
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Dashboard page — PAGE-002
 *
 * Four tabs:
 *   1. Overview (default) — job history, KPI stats
 *   2. Needs Review — human review queue (FR-077)
 *   3. Portfolio Risk — cross-contract risk summary (FR-086)
 *   4. Team Monitoring — admin-only performance metrics (FR-083/084)
 *
 * Active tab persisted in URL search param ?tab= for deep-linking and
 * browser back/forward navigation.
 *
 * FR-077: All gate decisions require human review — Needs Review tab shows
 * ALL completed jobs pending human sign-off, regardless of AI recommendation.
 *
 * WCAG 2.1 AA: tablist/tab/tabpanel pattern, keyboard arrows, h1 focus on mount.
 */
export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: TabId = isValidTab(rawTab) ? rawTab : 'overview';

  const h1Ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => { h1Ref.current?.focus(); }, []);

  const { isAdmin } = useAuth();
  const [itemsWaitingOnMe, setItemsWaitingOnMe] = useState<number | null>(null);

  const {
    data: queueStats,
    error: queueStatsError,
  } = useReviewQueueStats();

  // Portfolio risk count for tab badge — only fetch when relevant
  const { data: portfolioRisk } = usePortfolioRisk();
  // The three counts are typed as required numbers, but that is a claim about
  // the API, not a guarantee: a response missing any one of them sums to NaN.
  // Fall back to no badge rather than showing the reviewer a meaningless number.
  const portfolioRiskSum =
    portfolioRisk != null
      ? (portfolioRisk.overdue_review_count +
         portfolioRisk.unaddressed_alerts_count +
         portfolioRisk.expiring_contracts_count)
      : null;
  const portfolioRiskCount = Number.isFinite(portfolioRiskSum) ? portfolioRiskSum : null;

  function setActiveTab(id: TabId) {
    // Non-admins cannot navigate to team-monitoring
    if (id === 'team-monitoring' && !isAdmin) return;
    setSearchParams({ tab: id }, { replace: true });
  }

  return (
    <div className="space-y-6">
      {/* Page heading + primary action */}
      <div className="flex items-center justify-between">
        <h1
          ref={h1Ref}
          tabIndex={-1}
          className="text-2xl font-bold text-foreground focus-visible:outline-none"
        >
          Dashboard
        </h1>
        <SubmitContractDialog />
      </div>

      {/* Tab navigation */}
      <DashboardTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        reviewQueueCount={queueStats?.total ?? null}
        reviewQueueError={Boolean(queueStatsError)}
        portfolioRiskCount={portfolioRiskCount}
        itemsWaitingOnMe={itemsWaitingOnMe}
        isAdmin={isAdmin}
      />

      {/* Tab panels */}
      <div
        id="tabpanel-overview"
        role="tabpanel"
        aria-labelledby="tab-overview"
        hidden={activeTab !== 'overview'}
      >
        {activeTab === 'overview' && (
          <OverviewTab onGoToNeedsReview={() => { setActiveTab('needs-review'); }} />
        )}
      </div>

      <div
        id="tabpanel-pipeline-activity"
        role="tabpanel"
        aria-labelledby="tab-pipeline-activity"
        hidden={activeTab !== 'pipeline-activity'}
      >
        {activeTab === 'pipeline-activity' && (
          <PipelineActivityTab onCountChange={setItemsWaitingOnMe} />
        )}
      </div>

      <div
        id="tabpanel-needs-review"
        role="tabpanel"
        aria-labelledby="tab-needs-review"
        hidden={activeTab !== 'needs-review'}
      >
        {activeTab === 'needs-review' && <NeedsReviewTab />}
      </div>

      <div
        id="tabpanel-portfolio-risk"
        role="tabpanel"
        aria-labelledby="tab-portfolio-risk"
        hidden={activeTab !== 'portfolio-risk'}
      >
        {activeTab === 'portfolio-risk' && <PortfolioRiskTab />}
      </div>

      <div
        id="tabpanel-team-monitoring"
        role="tabpanel"
        aria-labelledby="tab-team-monitoring"
        hidden={activeTab !== 'team-monitoring'}
      >
        {activeTab === 'team-monitoring' && <TeamMonitoringTab />}
      </div>
    </div>
  );
}
